import { computed, watch } from 'vue';
import { defineStore, acceptHMRUpdate, storeToRefs } from 'pinia';
import {
  usePositionsQuery,
  usePositionMutations,
  useArchivedPositionsQuery,
  useEnsurePositionData,
} from '@/queries/positions';
import { useIndexById } from '@/composables/useIndexById';
import { useOrderStore } from '@/stores/order';
import { usePositionFilesInReviewForUser } from '@/components/elements/Positions/usePositionFilesInReviewForUser';
import { useActiveUserStore } from './user';
import { rollUpProductMaturity } from '@cutr/constants/position';

export const usePositionsStore = defineStore('positions', () => {
  const orderStore = useOrderStore();
  const { activeVariantId, currentOrder, currentVariant } =
    storeToRefs(orderStore);
  const { isAdmin } = storeToRefs(useActiveUserStore());
  const { filesNeedReview } = usePositionFilesInReviewForUser();
  const isPositionsOrder = computed(() =>
    Boolean(currentOrder.value.isPositions)
  );
  const archivedEnabled = computed(
    () => isPositionsOrder.value && isAdmin.value
  );
  const { positions, query } = usePositionsQuery(activeVariantId, {
    enabled: isPositionsOrder,
  });
  const { positions: archivedPositions } = useArchivedPositionsQuery(
    activeVariantId,
    {
      enabled: archivedEnabled,
    }
  );
  const ensureData = useEnsurePositionData();
  const { invalidateOnMove, ...mutations } = usePositionMutations(
    () => currentOrder.value.id
  );

  const isLoading = computed(() => query.isLoading.value);
  const isSuccess = computed(() => query.isSuccess.value);

  const allPositions = computed(() => [
    ...positions.value,
    ...archivedPositions.value,
  ]);

  const getById = useIndexById(allPositions);

  // Mirrors the server's roll-up, so the variant's maturity follows position edits without reloading the order.
  // Null when no position has one.
  const positionsProductMaturity = computed(() =>
    rollUpProductMaturity(
      positions.value.filter((p) => p.isInScope).map((p) => p.designMaturity)
    )
  );
  const variantProductMaturity = computed(
    () =>
      positionsProductMaturity.value ??
      currentVariant.value?.productMaturity ??
      null
  );
  // The server stores every non-null roll-up, so keep the loaded variant in step for when positions stop deciding.
  watch(
    [positionsProductMaturity, currentVariant],
    ([value, variant]) => {
      if (value && variant) variant.productMaturity = value;
    },
    { immediate: true }
  );

  return {
    positions,
    isLoading,
    isSuccess,
    getById,
    filesNeedReview,
    archivedPositions,
    allPositions,
    positionsProductMaturity,
    variantProductMaturity,
    ensureData: () => ensureData(activeVariantId.value!),
    ...mutations,
    // Bound to the active variant, so a component never names one.
    invalidateOnMove: () => invalidateOnMove(activeVariantId.value ?? ''),
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(usePositionsStore, import.meta.hot));
}
